import { LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { FormEvent } from '../types/form-event.enum';

export abstract class FormFieldInput<T = unknown> extends LitElement {
  @property({ reflect: true, attribute: 'gz-form-input' })
  gzFormInput = '';

  @property({ type: Boolean, reflect: true, attribute: true })
  disabled = false;

  @property({ type: Boolean, reflect: true, attribute: true })
  readonly = false;

  @property({ type: String, attribute: true, reflect: true })
  errorTooltipText = '';

  protected invalid = false;

  /**
   * Attribute used to mark the component as invalid. Decoupled from the invalid property to prevent any issues
   * where the user could delete the attribute and so the component would become valid.
   * Used for tests.
   */
  private invalidAttributeName = 'data-invalid';

  public abstract setFormValue(value: T): void;

  public abstract getFormValue(): T;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('unhandledrejection', console.error);
  }

  public setEnabled(isEnabled: boolean): void {
    this.disabled = !isEnabled;
    this.emitOnDisableInputEvent();
  }

  public setReadonly(isReadonly: boolean): void {
    this.readonly = isReadonly;
  }

  public markAsInvalid(): void {
    if (this.invalid) {
      return;
    }

    this.invalid = true;
    this.requestUpdate('invalid', false);
    this.setInvalidAttribute(this.invalid);
  }

  public markAsValid(): void {
    if (!this.invalid) {
      return;
    }

    this.invalid = false;
    this.requestUpdate('invalid', true);
    this.setInvalidAttribute(this.invalid);
  }

  private setInvalidAttribute(invalid: boolean): void {
    if (invalid) {
      this.setAttribute(this.invalidAttributeName, '');
      return;
    }

    this.removeAttribute(this.invalidAttributeName);
  }

  attributeChangedCallback(name, oldVal, newVal) {
    super.attributeChangedCallback(name, oldVal, newVal);

    if (name === 'disabled') {
      this.emitOnDisableInputEvent();
    }
  }

  private emitOnDisableInputEvent() {
    this.dispatchEvent(
      new CustomEvent<{ disabled: boolean }>(FormEvent.OnDisableInput, {
        detail: {
          disabled: this.disabled,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Method used form comparing two form field values.
   * For value types more complex, this method should be overriding.
   * @param value1
   * @param value2
   */
  public areFormValuesEqual(value1: T, value2: T) {
    return value1 === value2;
  }
}
